import { ArrowRight, Check, X } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { LocalizedAnchor } from '@/components/LocalizedLink';
import { buttonVariants } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { JsonLd } from '@/components/JsonLd';
import { useT } from '@/i18n/provider';
import { NotFoundPage } from '@/pages/NotFoundPage';
import {
  comparisonRows,
  COMPETITORS,
  duos,
  type CellValue,
  type CompetitorId,
} from '@/data/compare-data';

function useResolveCell() {
  const t = useT();
  return (value: CellValue): React.ReactNode => {
    if (value === true) {
      return (
        <span className="inline-flex items-center gap-1 text-emerald-400 font-medium">
          <Check className="h-4 w-4" /> {t('compare.cell.included')}
        </span>
      );
    }
    if (value === false) {
      return (
        <span className="inline-flex items-center gap-1 text-red-400">
          <X className="h-4 w-4" /> {t('compare.cell.no')}
        </span>
      );
    }
    if (typeof value === 'string' && value.startsWith('t:')) {
      return <span>{t(`compare.cell.${value.slice(2)}`)}</span>;
    }
    return <span>{value}</span>;
  };
}

function fillTemplate(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), v), template);
}

export function CompareDuoPage() {
  const { slug } = useParams<{ slug: string }>();
  const t = useT();
  const resolveCell = useResolveCell();

  const duo = duos.find((d) => d.slug === slug);
  if (!duo) return <NotFoundPage />;

  const competitorNames = duo.competitors.map((id) => COMPETITORS[id].name);
  const Icon = duo.icon;

  // For 2-way duos use {a} {b}, for 3-way use {a} {b} {c}.
  const introVars: Record<string, string> = duo.competitors.length === 3
    ? { a: competitorNames[0], b: competitorNames[1], c: competitorNames[2] }
    : { a: competitorNames[0], b: competitorNames[1] };

  const introTemplate = duo.competitors.length === 3
    ? t('compare.duo.intro3')
    : t('compare.duo.intro');

  useDocumentMeta({
    title: t(`compare.duo.${duo.slug}.title`),
    description: t(`compare.duo.${duo.slug}.metaDescription`),
  });

  // FAQ schema — substitute competitor names into the templated questions.
  const faqEntries = (['1', '2', '3', '4', '5'] as const).map((n) => {
    const q = fillTemplate(t(`compare.faq.q${n}`), introVars);
    const a = fillTemplate(t(`compare.faq.a${n}`), introVars);
    return { q, a };
  });

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqEntries.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'LogPulse', item: 'https://logpulse.io' },
      { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://logpulse.io/compare' },
      {
        '@type': 'ListItem',
        position: 3,
        name: t(`compare.duo.${duo.slug}.heading`),
        item: `https://logpulse.io/compare/${duo.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-12 sm:pt-40 sm:pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/compare"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6 hover:bg-primary-500/20 transition-colors"
          >
            <Icon className="h-4 w-4" />
            {t('compare.duo.heroBadge')}
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t(`compare.duo.${duo.slug}.heading`)}
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            {fillTemplate(introTemplate, introVars)}
          </p>
        </div>
      </section>

      {/* Comparison table — only show LogPulse + selected competitors */}
      <section className="pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            {t('compare.duo.tableTitle')}
          </h2>
          <div className="rounded-2xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-900/80 border-b border-gray-800">
                    <th className="text-left p-4 text-gray-400 font-semibold min-w-[180px]">
                      {t('compare.tableHeader.feature')}
                    </th>
                    <th className="text-left p-4 min-w-[150px]">
                      <span className="text-primary-400 font-bold">LogPulse</span>
                    </th>
                    {duo.competitors.map((id) => (
                      <th key={id} className="text-left p-4 text-gray-300 font-semibold min-w-[150px]">
                        {COMPETITORS[id].name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows
                    .filter((row) => !duo.rowFilter || duo.rowFilter.includes(row.featureKey))
                    .map((row) => (
                      <tr key={row.featureKey} className="border-b border-gray-800/50 hover:bg-gray-900/30 transition-colors">
                        <td className="p-4 text-white font-medium">{t(`compare.rows.${row.featureKey}`)}</td>
                        <td className="p-4 text-primary-300 font-medium bg-primary-500/5">
                          {resolveCell(row.logpulse)}
                        </td>
                        {duo.competitors.map((id: CompetitorId) => (
                          <td key={id} className="p-4 text-gray-400">
                            {resolveCell(row[id])}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 border-t border-gray-800/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            {t('compare.duo.faqTitle')}
          </h2>
          <div className="space-y-6">
            {faqEntries.map(({ q, a }, i) => (
              <div key={i} className="rounded-xl bg-gray-900/50 border border-gray-800 p-6">
                <h3 className="text-lg font-semibold text-white mb-3">{q}</h3>
                <p className="text-gray-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 border-t border-gray-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto rounded-2xl bg-gradient-to-b from-primary-500/10 to-gray-900/50 border border-primary-500/20 p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('compare.cta.title')}
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed max-w-xl mx-auto">
              {t('compare.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.logpulse.io/sign-in"
                className={buttonVariants({ variant: 'gradient', size: 'lg' })}
              >
                {t('compare.cta.primary')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </a>
              <LocalizedAnchor href="/docs" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                {t('compare.cta.secondary')}
              </LocalizedAnchor>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
